import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import { useTheme } from '../../../../theme/hooks'
import Calendar from '../../../shared/Calendar'

type Props = {
  selectedDate: Date | null
  selectedDateString: string
  onChange: (date: Date, isoDateString: string) => void
}

const CALENDAR_HEIGHT = 320

const DateFieldBase = ({
  selectedDate,
  selectedDateString,
  onChange,
}: Props) => {
  const { theme } = useTheme()
  const [showCalendar, setShowCalendar] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })
  const inputRef = useRef<HTMLDivElement | null>(null)

  const openCalendar = useCallback(() => {
    const node = inputRef.current
    if (node) {
      const rect = node.getBoundingClientRect()
      const openAbove = rect.bottom + CALENDAR_HEIGHT > window.innerHeight
      setPosition({
        top: openAbove ? rect.top - CALENDAR_HEIGHT - 4 : rect.bottom + 4,
        left: rect.left,
      })
    }
    setShowCalendar(true)
  }, [])

  useEffect(() => {
    if (!showCalendar) return undefined
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element | null
      if (target && !target.closest('.calendar-container')) {
        setShowCalendar(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showCalendar])

  const handleCalendarChange = useCallback(
    (value: unknown) => {
      if (typeof value === 'string') {
        const [year, month, day] = value.split('-').map(Number)
        if ([year, month, day].every(Number.isFinite)) {
          onChange(new Date(year, month - 1, day), value)
        }
      }
      setShowCalendar(false)
    },
    [onChange],
  )

  return (
    <div>
      <label
        className="text-sm font-medium"
        style={{ color: theme.text.primary }}
      >
        Date
      </label>
      <div className="relative mt-1" ref={inputRef}>
        <input
          type="text"
          readOnly
          value={selectedDate ? selectedDate.toLocaleDateString() : ''}
          placeholder="Sélectionnez une date"
          className="w-full cursor-pointer rounded-md border px-3 py-2 text-sm focus:outline-none"
          style={{
            borderColor: theme.input.border,
            backgroundColor: theme.input.bg,
            color: theme.input.text,
          }}
          onFocus={e => {
            e.currentTarget.style.borderColor = theme.input.borderFocus
            e.currentTarget.style.backgroundColor = theme.input.bgFocus
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = theme.input.border
            e.currentTarget.style.backgroundColor = theme.input.bg
          }}
          onClick={openCalendar}
        />
        <FontAwesomeIcon
          icon={faCalendarAlt}
          className="absolute right-3 top-1/2 -translate-y-1/2 transform"
          style={{ color: theme.text.tertiary }}
        />
      </div>

      {showCalendar &&
        ReactDOM.createPortal(
          <div
            className="calendar-container fixed z-[100000] rounded-lg border shadow-lg"
            style={{
              top: position.top,
              left: position.left,
              backgroundColor: theme.surface?.primary ?? theme.modal?.bg,
              borderColor: theme.section?.border,
              boxShadow: theme.modal?.shadow,
            }}
          >
            <Calendar
              value={selectedDateString}
              onChange={handleCalendarChange}
              mode="single"
            />
          </div>,
          document.body,
        )}
    </div>
  )
}

export const DateField = memo(DateFieldBase)
